// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Results component for the item data upload process
 */
import { Link } from '@/components/link';
import {
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@/constants/hardfin-standard-icons';
import type {
  ItemDataUploadResult,
  ProcessedItemImportData,
} from '@/items/api/upload-item-data';
import type { ExpandedItemsState } from '@/items/components/item-data-upload/types';
import { SparseItem } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  createIdentifierSuffix,
  formatFailedRowsForCopy,
} from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

const ITEM_HEIGHT = 40;
const FAILED_ROW_HEIGHT = 80;

interface ResultsProps {
  uploadResult: ItemDataUploadResult | null;
  expandedItems: ExpandedItemsState;
  onToggleExpanded: (section: keyof ExpandedItemsState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowFailure {
  rowNumber: number;
  itemName: string;
  errors: string[];
}

interface ItemRowProps {
  index: number;
  style: React.CSSProperties;
  data: SparseItem[];
}

const ItemRow = ({ index, style, data }: ItemRowProps) => {
  const item = data[index];
  // Determine the route segment based on item properties
  const typeSegment = !item.isPhysical
    ? 'service'
    : item.hasAssets
      ? 'device'
      : 'bulk';
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Link
              to={`/catalog/items/$itemKey`}
              params={{ itemKey: item.key }}
              search={{ type: typeSegment }}
            >
              {item.name} ({item.sku})
            </Link>
          }
        />
      </ListItem>
    </div>
  );
};

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: RowFailure[];
}

const FailedRowItem = ({ index, style, data }: FailedRowItemProps) => {
  const row = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={`For file row ${row.rowNumber} (item "${row.itemName || 'Unknown'}"):`}
          secondary={
            <Stack spacing={1} mt={1}>
              {row.errors.map((error: string, idx: number) => (
                <Typography key={idx} variant="body2" color="error">
                  {error}
                </Typography>
              ))}
            </Stack>
          }
        />
      </ListItem>
    </div>
  );
};

/**
 * Renders the results step of the item data upload process
 */
export function Results({
  uploadResult,
  expandedItems,
  onToggleExpanded,
  onBack,
  onStartNew,
  onClose,
}: ResultsProps) {
  const [isCopied, setIsCopied] = useState(false);

  if (!uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, items, structureErrors, rowErrors } = uploadResult;

  const createdItems: SparseItem[] = [];
  const failedRows: RowFailure[] = [];

  // Process items array for created items and per-item errors
  items.forEach((item: ProcessedItemImportData) => {
    if (item.errors.length > 0) {
      failedRows.push({
        rowNumber: item.index,
        itemName: item.itemName,
        errors: item.errors.map(
          (err) => `Column "${err.fieldName}" has error: ${err.error}`
        ),
      });
      return;
    }
    if (item.created && item.validatedItem) {
      createdItems.push(item.validatedItem);
    }
  });

  // Process rowErrors array for validation errors
  rowErrors.forEach((row) => {
    failedRows.push({
      rowNumber: row.index,
      itemName: '',
      errors: row.errors.map(
        (err) => `Column "${err.fieldName}" has error: ${err.error}`
      ),
    });
  });

  /**
   * Handles copying all failed row errors to clipboard with visual feedback
   */
  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const text = formatFailedRowsForCopy(failedRows, (row) =>
        createIdentifierSuffix(row, 'itemName', 'name')
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  return (
    <Card>
      <CardContent>
        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                Successfully created{' '}
                {pluralize(createdItems.length, {
                  singular: 'item',
                  plural: 'items',
                })}
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: 'success.light',
                  color: 'success.contrastText',
                  cursor: createdItems.length ? 'pointer' : undefined,
                }}
                onClick={
                  createdItems.length
                    ? () => onToggleExpanded('created')
                    : undefined
                }
              >
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">
                    Created items ({formatNumber(createdItems.length)})
                  </Typography>
                </Box>
                {createdItems.length > 0 &&
                  (expandedItems.created ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  ))}
              </Box>
              <Collapse in={expandedItems.created}>
                <VirtualList
                  height={Math.min(createdItems.length * ITEM_HEIGHT, 200)}
                  itemCount={createdItems.length}
                  itemSize={ITEM_HEIGHT}
                  itemData={createdItems}
                  width="100%"
                >
                  {ItemRow}
                </VirtualList>
              </Collapse>
            </Paper>
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {structureErrors.length > 0 && (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that are not recognized
                    or is missing required columns
                  </Typography>
                </>
              )}
              {failedRows.length > 0 && structureErrors.length === 0 && (
                <>
                  <AlertTitle>Uploaded data invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes data that does not meet
                    validation requirements
                  </Typography>
                </>
              )}
            </Alert>

            {structureErrors && structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Structure errors ({formatNumber(structureErrors.length)})
                    </Typography>
                  </Box>
                  {expandedItems.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={expandedItems.structureErrors}>
                  <ul
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                      paddingLeft: '40px',
                    }}
                  >
                    {structureErrors.map((err, idx) => (
                      <li
                        key={`structure-${idx}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        Column "{err.fieldName}": {err.error}
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Paper>
            )}

            {failedRows && failedRows.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Invalid rows ({formatNumber(failedRows.length)}{' '}
                      {pluralize(failedRows.length, {
                        includeCount: false,
                        singular: 'row',
                        plural: 'rows',
                      })}
                      )
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {expandedItems.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={expandedItems.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      300
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={failedRows}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between">
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <Box display="flex">
            <Button variant="outlined" onClick={onStartNew} sx={{ mr: 1 }}>
              Start new upload
            </Button>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
